import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, MapPin, Star, Flame, Leaf, Soup, ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { SITE, MARQUEE } from '../data/site';
import { useMenuData } from '../lib/useMenuData';
import { money } from '../lib/util';
import { SectionHead, Reveal, Diet } from '../components/Bits';
import { Mandala } from '../components/Art';
import { Img } from '../components/Img';
import { Gallery } from '../components/Gallery';
import { Reviews } from '../components/Reviews';

const HERO_SLIDES = [
  { src: 'https://dtwaeonhht2im.cloudfront.net/4d249a04cf6d1d973efe8de3ec78a0f5.png', alt: 'A lavish platter of Dindugal mutton biryani' },
  { src: 'https://dtwaeonhht2im.cloudfront.net/52c0b11fd3b1d1cfe7b099cad173a35b.jpg', alt: 'A full spread of South Indian curries and breads' },
  { src: 'https://dtwaeonhht2im.cloudfront.net/66ca5429f16878ee1b0751be93cbc866.png', alt: 'Flaky parotta with a rich curry' },
  { src: 'https://dtwaeonhht2im.cloudfront.net/d678fb6d5baad869879cc752e58e8237-1.jpeg', alt: 'A rich Chettinad chicken curry' },
  { src: 'https://dtwaeonhht2im.cloudfront.net/c1586e6d1af4f2e9910af054b0ef248e-1.jpeg', alt: 'A crispy dosa on a banana leaf' },
];

function HeroSlideshow() {
  const [active, setActive] = useState(0);
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => setActive((i) => (i + 1) % HERO_SLIDES.length), 5000);
    return () => window.clearInterval(id);
  }, [reduce]);

  return (
    <>
      <div className="absolute inset-0" aria-hidden>
        {HERO_SLIDES.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt=""
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              opacity: i === active ? 1 : 0,
              transition: 'opacity 1.3s ease-in-out',
              animation: !reduce && i === active ? 'kenburns 6s ease-out forwards' : 'none',
            }}
          />
        ))}
      </div>
      {/* Slide indicators */}
      <div className="absolute right-5 top-1/2 -translate-y-1/2 z-10 hidden md:flex flex-col gap-2.5">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.src}
            onClick={() => setActive(i)}
            aria-label={`Show slide ${i + 1}`}
            className="rounded-full transition-all duration-300"
            style={{
              width: 8,
              height: i === active ? 26 : 8,
              background: i === active ? 'var(--color-accent)' : 'rgba(255,255,255,0.55)',
            }}
          />
        ))}
      </div>
    </>
  );
}

function Hero() {
  const { itemCount } = useMenuData();
  return (
    <section className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden">
      <div className="absolute inset-0" aria-hidden>
        <img
          src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-09/25b21e08ce30.png"
          alt="Parotta Express authentic South Indian food"
          loading="eager"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(180deg, rgba(20,13,8,0.72) 0%, rgba(20,13,8,0.28) 32%, rgba(20,13,8,0.35) 62%, rgba(20,13,8,0.92) 100%)' }}
      />
      <div className="wrap relative pb-16 pt-32 md:pb-24">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5" style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.24)', backdropFilter: 'blur(6px)', color: '#fff' }}>
            <Star size={14} fill="var(--color-accent)" style={{ color: 'var(--color-accent)' }} />
            <span className="text-[0.8rem] font-semibold tracking-wide">Authentic South Indian · Coppell, TX</span>
          </div>

          <h1 className="font-display mt-6 text-white" style={{ fontSize: 'clamp(2.9rem, 7.5vw, 5.6rem)', lineHeight: 0.98, letterSpacing: '-0.03em', textShadow: '0 2px 30px rgba(0,0,0,0.4)' }}>
            Flaky parottas,<br />
            <span style={{ color: 'var(--color-accent)' }}>fiery</span> Chettinad curries.
          </h1>

          <p className="mt-6 max-w-lg" style={{ fontSize: '1.18rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)', textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}>
            {SITE.tagline} — hand-slapped parottas, dosas, dum biryanis and spice-forward
            curries, made fresh every single day.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/menu" className="btn btn-primary">
              Order Online <ArrowRight size={17} />
            </Link>
            <Link
              to="/menu"
              className="btn"
              style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.32)', color: '#fff', backdropFilter: 'blur(6px)' }}
            >
              View the Menu
            </Link>
          </div>

          <div className="mt-9 flex flex-wrap gap-x-7 gap-y-3 text-[0.92rem]" style={{ color: 'rgba(255,255,255,0.85)' }}>
            <a href={SITE.address.mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-white">
              <MapPin size={16} style={{ color: 'var(--color-accent)' }} /> {SITE.address.line1}
            </a>
            <span className="inline-flex items-center gap-2">
              <Clock size={16} style={{ color: 'var(--color-accent)' }} /> Open today · until 11:00 PM
            </span>
            <span className="inline-flex items-center gap-2">
              <Flame size={16} style={{ color: 'var(--color-accent)' }} /> {itemCount}+ dishes
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const row = [...MARQUEE, ...MARQUEE];
  return (
    <div className="marquee relative overflow-hidden py-5 md:py-6" style={{ background: 'var(--color-ink)' }}>
      <div className="marquee-track items-center gap-10">
        {row.map((w, i) => (
          <span key={i} className="inline-flex items-center gap-10 whitespace-nowrap">
            <span className="font-display text-lg md:text-xl" style={{ color: 'var(--color-ink-inverse)' }}>{w}</span>
            <span aria-hidden style={{ color: 'var(--color-accent)' }}>✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const FEATURES = [
  { img: 'https://dtwaeonhht2im.cloudfront.net/66ca5429f16878ee1b0751be93cbc866.png', name: 'Parotta', price: 15.99, blurb: 'Hand-slapped, buttery-layered flatbread folded fresh with a rich salna.' },
  { img: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-08-07/224e874d4fb3.png', name: 'Gobi 65', price: 10.99, blurb: 'Crisp, spiced cauliflower florets deep-fried with curry leaves.' },
  { img: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/8d073425-a70c-42c7-a3ed-1a2c0386029b/1786121757305-owj9kk.png', name: 'Chicken Pepper Fry', price: 14.99, blurb: 'Tender chicken pieces pan-roasted with freshly crushed black pepper and shallots.' },
  { img: 'https://dtwaeonhht2im.cloudfront.net/c1586e6d1af4f2e9910af054b0ef248e-1.jpeg', name: 'Dosa', price: 10.99, blurb: 'Paper-crisp fermented crêpe served with sambar and fresh chutneys.' },
  { img: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-09/4fb2332a8759.png', name: 'Idly', price: 8.99, blurb: 'Pillow-soft steamed rice cakes with sambar and coconut chutney.' },
  { img: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-09/db546c6065aa.png', name: 'Biryani (Seeraga Samba)', price: 18.99, blurb: 'Short-grain seeraga samba rice, sealed and slow-cooked on dum.' },
  { img: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-09/2dcab759b511.png', name: 'Botti Fry', price: 18.99, blurb: 'Chettinad-style dry-fried mutton, dark and peppery with curry leaf.' },
];

function Featured() {
  const trackRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const resumeTimeout = useRef<ReturnType<typeof setTimeout>>();

  const scrollByCard = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-card]');
    const step = (card?.offsetWidth ?? 280) + 24;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
    pausedRef.current = true;
    clearTimeout(resumeTimeout.current);
    resumeTimeout.current = setTimeout(() => { pausedRef.current = false; }, 4000);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    let raf: number;
    const SPEED = 0.6; // px per frame

    const tick = () => {
      if (!pausedRef.current) {
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll > 0) {
          if (el.scrollLeft >= maxScroll - 1) {
            el.scrollLeft = 0;
          } else {
            el.scrollLeft += SPEED;
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onEnter = () => { pausedRef.current = true; };
    const onLeave = () => { pausedRef.current = false; };
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('touchstart', onEnter, { passive: true });
    el.addEventListener('touchend', onLeave);

    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('touchstart', onEnter);
      el.removeEventListener('touchend', onLeave);
      clearTimeout(resumeTimeout.current);
    };
  }, []);

  return (
    <section className="py-20 md:py-28 overflow-hidden">
      <div className="wrap flex flex-wrap items-end justify-between gap-6 mb-10">
        <SectionHead kicker="Guest Favorites" title="Dishes people drive in for" intro="A few of the plates our regulars order on repeat." />
        <div className="hidden sm:flex items-center gap-2">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            aria-label="Scroll left"
            className="grid place-items-center w-11 h-11 rounded-full transition-colors hover:brightness-95"
            style={{ background: 'var(--color-surface-2)', color: 'var(--color-ink)' }}
          >
            <ChevronLeft size={19} />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            aria-label="Scroll right"
            className="grid place-items-center w-11 h-11 rounded-full transition-colors hover:brightness-95"
            style={{ background: 'var(--color-surface-2)', color: 'var(--color-ink)' }}
          >
            <ChevronRight size={19} />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="flex gap-5 md:gap-6 overflow-x-auto pb-4 pl-4 pr-4 md:pl-[max(1.5rem,calc((100vw-1180px)/2))] md:pr-[max(1.5rem,calc((100vw-1180px)/2))]"
        style={{ scrollbarWidth: 'none', scrollBehavior: 'auto' }}
      >
        {FEATURES.map((f, i) => (
          <Reveal
            as="div"
            key={f.name}
            delay={i * 70}
            data-card
            className="shrink-0 w-[72vw] xs:w-[280px] sm:w-[300px]"
          >
            <Link to="/menu" className="group card block overflow-hidden h-full transition-all duration-300 hover:-translate-y-1.5" style={{ boxShadow: 'var(--pe-shadow)' }}>
              <div className="relative">
                <Img src={f.img} alt={f.name} className="aspect-[4/3]" imgClassName="group-hover:scale-105" />
                <span className="absolute top-3 right-3 tnum rounded-full px-2.5 py-1 text-[0.8rem] font-semibold" style={{ background: 'rgba(255,255,255,0.92)', color: 'var(--color-accent-deep)' }}>
                  from {money(f.price)}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-xl text-ink">{f.name}</h3>
                  <ArrowUpRight size={18} className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: 'var(--color-accent)' }} />
                </div>
                <p className="mt-2 text-[0.94rem] text-ink-2 leading-relaxed">{f.blurb}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Specialty() {
  return (
    <section className="py-20 md:py-28" style={{ background: 'var(--color-surface-2)' }}>
      <div className="wrap grid items-center gap-12 md:grid-cols-2">
        <Reveal delay={80} className="order-1">
          <div className="relative">
            <Img src="https://dtwaeonhht2im.cloudfront.net/66ca5429f16878ee1b0751be93cbc866.png" alt="Flaky parotta served with a rich curry" className="aspect-[5/6] rounded-[var(--pe-radius-lg)]" imgClassName="hover:scale-[1.03]" />
            <div className="absolute -bottom-5 -right-4 md:-right-6 card px-5 py-4" style={{ boxShadow: 'var(--pe-shadow-lg)' }}>
              <div className="font-display text-lg leading-none text-ink">Parotta + Chicken Salna</div>
              <div className="text-[0.8rem] text-muted mt-1">A house favorite · {money(15.99)}</div>
            </div>
          </div>
        </Reveal>

        <Reveal className="order-2">
          <p className="eyebrow">The House Specialty</p>
          <h2 className="font-display mt-3 text-ink" style={{ fontSize: 'clamp(2rem, 4.6vw, 3.2rem)', lineHeight: 1.04 }}>
            The parotta, done right.
          </h2>
          <p className="mt-5 text-ink-2" style={{ fontSize: '1.08rem', lineHeight: 1.7 }}>
            A great parotta is all in the wrist — dough slapped paper-thin, coiled into
            countless buttery layers, then griddled until it shatters into ribbons. We fold
            ours to order and serve them with a rich salna to tear, dip and share.
          </p>
          <ul className="mt-7 space-y-3">
            {[
              { icon: Flame, t: 'Ceylon Veechu & Kothu', d: 'Chopped, tossed and sizzled on the flat-top.' },
              { icon: Leaf, t: 'Lappa & Ooravecha', d: 'Stuffed and pot-steamed regional styles.' },
              { icon: Soup, t: 'Always with salna', d: 'Veg, egg, chicken or mutton gravy alongside.' },
            ].map(({ icon: Icon, t, d }) => (
              <li key={t} className="flex items-start gap-3.5">
                <span className="grid place-items-center w-9 h-9 rounded-full shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>
                  <Icon size={17} />
                </span>
                <div>
                  <div className="font-semibold text-ink">{t}</div>
                  <div className="text-[0.92rem] text-muted">{d}</div>
                </div>
              </li>
            ))}
          </ul>
          <Link to="/menu" className="btn btn-dark mt-8">Explore all parottas <ArrowRight size={16} /></Link>
        </Reveal>
      </div>
    </section>
  );
}

function Signatures() {
  const { signatures } = useMenuData();
  return (
    <section className="wrap py-20 md:py-28">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHead kicker="More to Love" title="Something for every craving" intro="Beyond the favorites — a few more from across the menu." />
        <Link to="/menu" className="hidden sm:inline-flex btn btn-ghost">Full menu <ArrowRight size={16} /></Link>
      </div>
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {signatures.filter((s) => !['Dindugal Mutton Biryani'].includes(s.name)).slice(0, 6).map((item, i) => (
          <Reveal as="div" key={item.name} delay={i * 50}>
            <article className="card h-full p-6 flex flex-col transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-start justify-between gap-3">
                <Diet tag={item.tag} className="mt-1" />
                <span className="tnum font-display text-lg" style={{ color: 'var(--color-accent-deep)' }}>{money(item.price)}</span>
              </div>
              <h3 className="font-display mt-3 text-xl text-ink leading-snug">{item.name}</h3>
              {item.desc && <p className="mt-2 text-[0.94rem] text-ink-2 leading-relaxed">{item.desc}</p>}
            </article>
          </Reveal>
        ))}
      </div>
      <div className="mt-10 sm:hidden">
        <Link to="/menu" className="btn btn-ghost w-full justify-center">Full menu <ArrowRight size={16} /></Link>
      </div>
    </section>
  );
}

function Story() {
  const { itemCount } = useMenuData();
  return (
    <section className="py-20 md:py-28" style={{ background: 'var(--color-surface-2)' }}>
      <div className="wrap grid gap-12 md:grid-cols-2 items-center">
        <Reveal>
          <div className="relative">
            <Img src="https://dtwaeonhht2im.cloudfront.net/52c0b11fd3b1d1cfe7b099cad173a35b.jpg" alt="A spread of South Indian curries and breads" className="aspect-[4/3] rounded-[var(--pe-radius-lg)]" imgClassName="hover:scale-[1.03]" />
            <div className="absolute -bottom-6 -left-4 md:-left-6 grid grid-cols-3 gap-3">
              {[
                { n: `${itemCount}+`, l: 'Dishes' },
                { n: '19', l: 'Sections' },
                { n: '100%', l: 'Fresh daily' },
              ].map((s) => (
                <div key={s.l} className="card px-4 py-3 text-center" style={{ boxShadow: 'var(--pe-shadow)' }}>
                  <div className="font-display text-xl" style={{ color: 'var(--color-accent)' }}>{s.n}</div>
                  <div className="text-[0.68rem] text-muted mt-0.5 whitespace-nowrap">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <SectionHead kicker="Our Story" title="A taste of home, brought to Coppell" />
          <div className="mt-5 space-y-4 text-ink-2" style={{ fontSize: '1.06rem', lineHeight: 1.7 }}>
            <p>{SITE.about[0]}</p>
            <p>{SITE.about[1]}</p>
          </div>
          <Link to="/about" className="btn btn-ghost mt-7">Read our story <ArrowRight size={16} /></Link>
        </Reveal>
      </div>
    </section>
  );
}

function OrderBand() {
  return (
    <section className="relative overflow-hidden">
      <Img src="https://dtwaeonhht2im.cloudfront.net/b1b95077a1ac4479c390339c647c58f5.png" alt="" className="absolute inset-0" imgClassName="" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(20,13,8,0.94) 0%, rgba(20,13,8,0.82) 55%, rgba(20,13,8,0.7) 100%)' }} />
      <div className="wrap relative py-20 md:py-28">
        <div className="max-w-2xl">
          <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Hungry Now?</p>
          <h2 className="font-display mt-3 text-white" style={{ fontSize: 'clamp(2rem, 5vw, 3.4rem)', lineHeight: 1.03 }}>
            Order online for pickup.
          </h2>
          <p className="mt-4 text-[1.05rem]" style={{ color: 'rgba(255,255,255,0.78)' }}>
            Build your order right here, pay securely, and skip the wait — ready for pickup at our Coppell kitchen.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/menu" className="btn btn-primary">Start your order <ArrowRight size={17} /></Link>
            <a href={SITE.phoneHref} className="btn" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.28)', color: '#fff' }}>
              or call {SITE.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function VisitTeaser() {
  return (
    <section className="wrap py-20 md:py-28">
      <div className="card overflow-hidden grid md:grid-cols-2">
        <div className="p-8 md:p-12">
          <SectionHead kicker="Come Sit With Us" title="Find Parotta Express" />
          <div className="mt-6 space-y-5">
            <div className="flex items-start gap-3">
              <MapPin size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-ink">{SITE.address.line1}</div>
                <div className="text-ink-2">{SITE.address.line2}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-ink">Open daily</div>
                <div className="text-ink-2">Mon–Thu &amp; Sun · 12 PM – 11 PM · Fri–Sat till midnight</div>
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={SITE.address.mapsUrl} target="_blank" rel="noreferrer" className="btn btn-primary">Get directions <ArrowRight size={16} /></a>
            <Link to="/visit" className="btn btn-ghost">Hours &amp; contact</Link>
          </div>
        </div>
        <div className="relative min-h-[300px]">
          <Img src="https://dtwaeonhht2im.cloudfront.net/cb5553fd522b0f35957fc0ba703f8382.png" alt="Pouring hot filter coffee" className="absolute inset-0" imgClassName="" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(200deg, transparent 30%, rgba(20,13,8,0.55) 100%)' }} />
          <Mandala className="absolute right-6 top-6 w-32 h-32 text-white opacity-40" />
          <div className="absolute left-6 bottom-6">
            <div className="font-display text-2xl text-white drop-shadow">Coppell, Texas</div>
            <div className="mt-1 text-sm" style={{ color: 'var(--color-gold)' }}>S MacArthur Blvd · Ste 112</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = 'Parotta Express – Authentic South Indian Cuisine in Coppell, TX';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Fresh flaky parottas, Chettinad curries, dosas, and dum biryanis made fresh daily in Coppell, Texas. Order online for pickup.');
  }, []);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": "https://parottaexpressusa.com/#webpage",
        "url": "https://parottaexpressusa.com/",
        "name": "Parotta Express – Authentic South Indian Cuisine in Coppell, TX",
        "description": "Fresh flaky parottas, Chettinad curries, dosas, and dum biryanis made fresh daily in Coppell, Texas. Order online for pickup.",
        "isPartOf": {
          "@id": "https://parottaexpressusa.com/#website"
        }
      }) }} />
      <Hero />
      <Marquee />
      <Featured />
      <Specialty />
      <Gallery />
      <Signatures />
      <Reviews />
      <Story />
      <OrderBand />
      <VisitTeaser />
    </>
  );
}
